/**
 * Arizona Special Education Configuration
 * Jurisdiction-aware category/subcategory definitions for MET Eligibility and IEP Services.
 * 
 * Architecture: Each state profile exports the same shape. The active profile is selected
 * by a jurisdiction key (default: 'AZ'). Future states add a new file and register in the
 * JURISDICTION_PROFILES map at the bottom.
 */

// =============================================================================
// MET Eligibility — Arizona Disability Categories
// =============================================================================
// Based on Arizona Administrative Code R7-2-401 and ADE Exceptional Student Services.
// selectionMode: 'single' = one subcategory | 'multi' = checkbox multi-select
// rules.minRequired: advisory minimum selections (shown in UI, not hard-enforced)

export const AZ_DISABILITY_CATEGORIES = [
  {
    id: 'autism',
    label: 'Autism',
    subcategories: null,
    selectionMode: null,
    rules: null,
  },
  {
    id: 'deaf_blindness',
    label: 'Deaf-Blindness',
    subcategories: null,
    selectionMode: null,
    rules: null,
  },
  {
    id: 'deafness',
    label: 'Deafness',
    subcategories: null,
    selectionMode: null,
    rules: null,
  },
  {
    id: 'developmental_delay',
    label: 'Developmental Delay',
    note: 'Ages 3 through 9 only',
    selectionMode: 'multi',
    rules: { minRequired: 2, advisory: 'Arizona typically requires delay in at least 2 developmental areas.' },
    subcategories: [
      { id: 'cognitive', label: 'Cognitive' },
      { id: 'communication', label: 'Communication' },
      { id: 'physical', label: 'Physical' },
      { id: 'social_emotional', label: 'Social-Emotional' },
      { id: 'adaptive', label: 'Adaptive' },
    ],
  },
  {
    id: 'emotional_disability',
    label: 'Emotional Disability',
    subcategories: null,
    selectionMode: null,
    rules: null,
  },
  {
    id: 'hearing_impairment',
    label: 'Hearing Impairment',
    subcategories: null,
    selectionMode: null,
    rules: null,
  },
  {
    id: 'intellectual_disability',
    label: 'Intellectual Disability',
    subcategories: null,
    selectionMode: null,
    rules: null,
  },
  {
    id: 'multiple_disabilities',
    label: 'Multiple Disabilities',
    subcategories: null,
    selectionMode: null,
    rules: null,
  },
  {
    id: 'multiple_disabilities_severe_sensory',
    label: 'Multiple Disabilities with Severe Sensory Impairment',
    subcategories: null,
    selectionMode: null,
    rules: null,
  },
  {
    id: 'orthopedic_impairment',
    label: 'Orthopedic Impairment',
    subcategories: null,
    selectionMode: null,
    rules: null,
  },
  {
    id: 'other_health_impairment',
    label: 'Other Health Impairment',
    subcategories: null,
    selectionMode: null,
    rules: null,
  },
  {
    id: 'preschool_severe_delay',
    label: 'Preschool Severe Delay',
    note: 'Ages 3 through 5 only (Arizona-specific)',
    selectionMode: 'multi',
    rules: { minRequired: 1, advisory: 'Select affected preschool developmental areas.' },
    subcategories: [
      { id: 'cognitive', label: 'Cognitive' },
      { id: 'communication', label: 'Communication' },
      { id: 'physical', label: 'Physical' },
      { id: 'social_emotional', label: 'Social-Emotional' },
      { id: 'adaptive', label: 'Adaptive' },
    ],
  },
  {
    id: 'specific_learning_disability',
    label: 'Specific Learning Disability',
    selectionMode: 'multi',
    rules: { minRequired: 1, advisory: 'Select all applicable SLD areas.' },
    subcategories: [
      { id: 'basic_reading_skills', label: 'Basic Reading Skills' },
      { id: 'reading_comprehension', label: 'Reading Comprehension' },
      { id: 'reading_fluency', label: 'Reading Fluency' },
      { id: 'written_expression', label: 'Written Expression' },
      { id: 'math_calculation', label: 'Math Calculation' },
      { id: 'math_problem_solving', label: 'Math Problem Solving' },
      { id: 'listening_comprehension', label: 'Listening Comprehension' },
      { id: 'oral_expression', label: 'Oral Expression' },
    ],
  },
  {
    id: 'speech_language_impairment',
    label: 'Speech-Language Impairment',
    subcategories: null,
    selectionMode: null,
    rules: null,
  },
  {
    id: 'traumatic_brain_injury',
    label: 'Traumatic Brain Injury',
    subcategories: null,
    selectionMode: null,
    rules: null,
  },
  {
    id: 'visual_impairment',
    label: 'Visual Impairment Including Blindness',
    subcategories: null,
    selectionMode: null,
    rules: null,
  },
];


// =============================================================================
// IEP Services — Specially Designed Instruction (SDI) Areas
// =============================================================================

export const AZ_SDI_CATEGORIES = [
  {
    id: 'academic',
    label: 'Academic',
    subcategories: [
      { id: 'reading', label: 'Reading' },
      { id: 'writing', label: 'Writing' },
      { id: 'math', label: 'Math' },
      { id: 'science', label: 'Science' },
      { id: 'social_studies', label: 'Social Studies' },
    ],
  },
  { id: 'communication_language', label: 'Communication / Language', subcategories: null },
  { id: 'behavior_social_emotional', label: 'Behavior / Social-Emotional', subcategories: null },
  { id: 'adaptive_functional', label: 'Adaptive / Functional Skills', subcategories: null },
  { id: 'vocational_transition', label: 'Vocational / Transition', subcategories: null },
  { id: 'pre_academic_developmental', label: 'Pre-academic / Developmental', subcategories: null },
];


// =============================================================================
// IEP Services — Related Services
// =============================================================================

export const AZ_RELATED_SERVICES = [
  {
    id: 'speech_language',
    label: 'Speech-Language',
    subcategories: [
      { id: 'articulation', label: 'Articulation' },
      { id: 'language', label: 'Language' },
      { id: 'fluency', label: 'Fluency' },
      { id: 'voice', label: 'Voice' },
      { id: 'aac', label: 'AAC (Augmentative & Alternative Communication)' },
    ],
  },
  { id: 'occupational_therapy', label: 'Occupational Therapy', subcategories: null },
  { id: 'physical_therapy', label: 'Physical Therapy', subcategories: null },
  { id: 'audiology', label: 'Audiology', subcategories: null },
  { id: 'vision_services', label: 'Vision Impairment Services', subcategories: null },
  { id: 'orientation_mobility', label: 'Orientation & Mobility', subcategories: null },
  { id: 'deaf_hoh_services', label: 'Deaf/Hard of Hearing Services', subcategories: null },
  { id: 'counseling_psychological', label: 'Counseling / Psychological Services', subcategories: null },
  { id: 'nursing_health', label: 'Nursing / Health Services', subcategories: null },
  { id: 'interpreting', label: 'Interpreting Services', subcategories: null },
  { id: 'assistive_technology', label: 'Assistive Technology Services', subcategories: null },
  { id: 'transportation', label: 'Transportation / Specialized Transportation', subcategories: null },
  { id: 'social_work', label: 'Social Work Services', subcategories: null },
  { id: 'parent_counseling_training', label: 'Parent Counseling and Training', subcategories: null },
  { id: 'recreation', label: 'Recreation / Therapeutic Recreation', subcategories: null },
  { id: 'medical_diagnostic', label: 'Medical Services (Diagnostic/Evaluation Only)', subcategories: null },
];


// =============================================================================
// IEP Services — Service Delivery Types
// =============================================================================

export const SERVICE_DELIVERY_TYPES = [
  { id: 'direct', label: 'Direct' },
  { id: 'consult', label: 'Consult' },
  { id: 'monitor', label: 'Monitor' },
  { id: 'collaborative', label: 'Collaborative' },
];


// =============================================================================
// Shared — Location / Setting options
// =============================================================================

export const SERVICE_LOCATIONS = [
  'General Education Classroom',
  'Resource Room',
  'Separate Classroom',
  'Separate School',
  'Home',
  'Hospital',
  'Community Setting',
  'Therapy Room',
  'Other',
];

export const FREQUENCY_UNITS = [
  'per week',
  'per month',
  'per quarter',
  'per year',
  'as needed',
];


// =============================================================================
// Jurisdiction Profile Map
// =============================================================================

const AZ_PROFILE = {
  id: 'AZ',
  label: 'Arizona',
  disabilityCategories: AZ_DISABILITY_CATEGORIES,
  sdiCategories: AZ_SDI_CATEGORIES,
  relatedServices: AZ_RELATED_SERVICES,
  serviceDeliveryTypes: SERVICE_DELIVERY_TYPES,
  serviceLocations: SERVICE_LOCATIONS,
  frequencyUnits: FREQUENCY_UNITS,
};

export const JURISDICTION_PROFILES = {
  AZ: AZ_PROFILE,
};

export const ACTIVE_JURISDICTION = 'AZ';

export function getActiveProfile() {
  return JURISDICTION_PROFILES[ACTIVE_JURISDICTION];
}
